import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { appDocumentTitle } from '../../appMeta';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import {
  useCreateCustomerMutation,
  useCustomerDetailQuery,
  useNavigationMenuQuery,
  useUpdateCustomerMutation,
} from '../../query/hooks';
import { CustomerFormAddressContactTabScreen } from './TabScreens/CustomerFormAddressContact';
import { CustomerFormCommercialNotesTabScreen } from './TabScreens/CustomerFormCommercialNotes';
import { CustomerFormIdentityTabScreen } from './TabScreens/CustomerFormIdentity';
import { CustomerFormOfficeWorkTabScreen } from './TabScreens/CustomerFormOfficeWork';
import { CustomerSamplesLocationsTabScreen } from './TabScreens/CustomerSamplesLocations';
import {
  customerFormIssues,
  customerFormTabLabel,
  customerRequiredFieldKeySet,
  type CustomerValidationMode,
} from './customerCompleteness';
import type { CustomerFormDraft } from './customerFormDraft';
import {
  customerFormDraftFromRow,
  draftToCustomerPayload,
  draftToNewCustomerPayload,
  emptyCustomerFormDraft,
} from './customerFormDraft';

type CustomerFormTabKey = 'identity' | 'address' | 'office' | 'commercial' | 'samples';

type SuccessDialogState =
  | { kind: 'created'; title: string; message: string; documentId: string }
  | { kind: 'updated'; title: string; message: string };

/** Routed pelanggan: `/customers/new`, `/customer/:documentId/edit`, `/customer/:documentId/view`. */
export function CustomerFormRoute() {
  const { jwt } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { documentId } = useParams<{ documentId: string }>();

  const pathNorm = pathname.replace(/\/$/, '');
  const isCreate = pathNorm === '/customers/new';
  const readOnly = /^\/customer\/[^/]+\/view$/.test(pathNorm);
  const detailId = !isCreate && documentId ? decodeURIComponent(documentId) : null;

  const detailQ = useCustomerDetailQuery(jwt, detailId);
  const menuQ = useNavigationMenuQuery(jwt);
  const createMut = useCreateCustomerMutation(jwt);
  const updateMut = useUpdateCustomerMutation(jwt);

  const showSamplesTab =
    Boolean(detailId) &&
    (menuQ.data?.access_codes?.includes('can_view_customer_sample') ?? true);

  const [draft, setDraft] = useState<CustomerFormDraft>(() => emptyCustomerFormDraft());
  const [tab, setTab] = useState<CustomerFormTabKey>('identity');
  const [successDialog, setSuccessDialog] = useState<SuccessDialogState | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  const pageHeading = readOnly ? 'Lihat pelanggan' : isCreate ? 'Pelanggan baru' : 'Ubah pelanggan';

  useDocumentTitle(appDocumentTitle(pageHeading));

  useEffect(() => {
    setTab('identity');
    setShowValidation(false);
  }, [isCreate, detailId]);

  useEffect(() => {
    if (tab === 'samples' && !showSamplesTab) {
      setTab('identity');
    }
  }, [tab, showSamplesTab]);

  useEffect(() => {
    if (isCreate) {
      setDraft(emptyCustomerFormDraft());
      return;
    }
    const row = detailQ.data;
    if (!row) return;
    setDraft(customerFormDraftFromRow(row));
  }, [isCreate, detailQ.data]);

  function patchDraft(patch: Partial<CustomerFormDraft>) {
    setDraft((d) => ({ ...d, ...patch }));
  }

  function onCancel() {
    navigate('/customers');
  }

  const validationMode: CustomerValidationMode = isCreate ? 'create' : 'edit';
  const requiredKeys = useMemo(() => customerRequiredFieldKeySet(validationMode), [validationMode]);

  const formIssues = useMemo(
    () => (showValidation && !readOnly ? customerFormIssues(draft, { mode: validationMode }) : []),
    [showValidation, readOnly, draft, validationMode],
  );
  const fieldErrors = useMemo(() => {
    const map: Partial<Record<keyof CustomerFormDraft, string>> = {};
    for (const issue of formIssues) map[issue.key] = issue.message;
    return map;
  }, [formIssues]);

  async function onSave() {
    if (!jwt) return;
    const issues = customerFormIssues(draft, { mode: validationMode });
    if (issues.length > 0) {
      setShowValidation(true);
      setTab(issues[0].tab);
      return;
    }

    try {
      if (isCreate) {
        const created = await createMut.mutateAsync(draftToNewCustomerPayload(draft));
        setSuccessDialog({
          kind: 'created',
          title: 'Pelanggan dibuat',
          message:
            created.customer_number != null && String(created.customer_number).trim() !== ''
              ? `Nomor pelanggan: ${created.customer_number}. Data telah disimpan.`
              : 'Data pelanggan telah disimpan.',
          documentId: created.documentId,
        });
        return;
      }
      if (!detailId) return;
      await updateMut.mutateAsync({ documentId: detailId, data: draftToCustomerPayload(draft) });
      setSuccessDialog({
        kind: 'updated',
        title: 'Tersimpan',
        message: 'Perubahan data pelanggan telah disimpan.',
      });
    } catch {
      /* surfaced below */
    }
  }

  function closeSuccessAndContinue() {
    if (!successDialog) return;
    if (successDialog.kind === 'created') {
      navigate(`/customer/${encodeURIComponent(successDialog.documentId)}/edit`, {
        replace: true,
      });
    }
    setSuccessDialog(null);
  }

  const savePending = createMut.isPending || updateMut.isPending;
  const saveDisabled = savePending;
  const mutationError = isCreate ? createMut.error : updateMut.error;

  if (!isCreate && (!detailId || detailId.trim() === '')) {
    return (
      <Box>
        <Alert severity="warning">ID pelanggan tidak valid.</Alert>
        <Button component={RouterLink} to="/customers" sx={{ mt: 2 }}>
          Kembali ke daftar
        </Button>
      </Box>
    );
  }

  if (!isCreate && detailQ.isPending) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!isCreate && detailQ.isError) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {detailQ.error instanceof Error ? detailQ.error.message : 'Gagal memuat pelanggan.'}
        </Alert>
        <Button component={RouterLink} to="/customers">
          Kembali ke daftar
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
          {pageHeading}
        </Typography>
        <Link component={RouterLink} to="/customers" underline="hover" variant="body2">
          Kembali ke daftar pelanggan
        </Link>
      </Box>

      {mutationError && !readOnly && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
          {mutationError instanceof Error ? mutationError.message : 'Gagal menyimpan.'}
        </Alert>
      )}

      {formIssues.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Lengkapi field yang wajib: {formIssues.map((issue) => issue.label).join(', ')}.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ px: 0.5, pt: 0, pb: 0 }}>
          <Tabs value={tab} onChange={(_, v: CustomerFormTabKey) => setTab(v)} sx={{ mb: 0 }}>
            <Tab
              label={customerFormTabLabel('Identitas', 'identity', formIssues)}
              value="identity"
              data-testid="customer-form-tab-identity"
            />
            <Tab
              label={customerFormTabLabel('Alamat & kontak', 'address', formIssues)}
              value="address"
              data-testid="customer-form-tab-address"
            />
            <Tab
              label={customerFormTabLabel('Kantor & pekerjaan', 'office', formIssues)}
              value="office"
              data-testid="customer-form-tab-office"
            />
            <Tab
              label={customerFormTabLabel('Komersial & catatan', 'commercial', formIssues)}
              value="commercial"
              data-testid="customer-form-tab-commercial"
            />
            {!isCreate && showSamplesTab ? (
              <Tab label="Samples (Locations)" value="samples" data-testid="customer-form-tab-samples" />
            ) : null}
          </Tabs>
        </Box>

        <Box sx={{ py: 2, flex: 1, minHeight: 0, overflow: 'auto' }}>
          {tab === 'identity' && (
            <CustomerFormIdentityTabScreen
              draft={draft}
              onChange={patchDraft}
              readOnly={readOnly}
              isNewCustomer={isCreate}
              fieldErrors={fieldErrors}
              requiredKeys={requiredKeys}
            />
          )}
          {tab === 'address' && (
            <CustomerFormAddressContactTabScreen
              draft={draft}
              onChange={patchDraft}
              readOnly={readOnly}
              fieldErrors={fieldErrors}
              requiredKeys={requiredKeys}
            />
          )}
          {tab === 'office' && (
            <CustomerFormOfficeWorkTabScreen
              draft={draft}
              onChange={patchDraft}
              readOnly={readOnly}
              fieldErrors={fieldErrors}
              requiredKeys={requiredKeys}
            />
          )}
          {tab === 'commercial' && (
            <CustomerFormCommercialNotesTabScreen
              draft={draft}
              onChange={patchDraft}
              readOnly={readOnly}
              fieldErrors={fieldErrors}
              requiredKeys={requiredKeys}
            />
          )}
          {tab === 'samples' && !isCreate && showSamplesTab && detailId ? (
            <CustomerSamplesLocationsTabScreen customerDocumentId={detailId} readOnly={readOnly} />
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 1,
            py: 1,
            flexShrink: 0,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Button variant="outlined" color="inherit" onClick={onCancel}>
            Batal
          </Button>
          {!readOnly && tab !== 'samples' && (
            <Button
              variant="contained"
              disabled={saveDisabled}
              onClick={() => void onSave()}
              data-testid="customer-form-save"
            >
              Simpan
            </Button>
          )}
        </Box>
      </Box>

      <Dialog open={Boolean(successDialog)} onClose={closeSuccessAndContinue} maxWidth="xs" fullWidth>
        {successDialog ? (
          <>
            <DialogTitle>{successDialog.title}</DialogTitle>
            <DialogContent>
              <Typography variant="body2">{successDialog.message}</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              {successDialog.kind === 'updated' ? (
                <Button variant="contained" onClick={closeSuccessAndContinue}>
                  OK
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={closeSuccessAndContinue}
                  data-testid="customer-created-open-edit"
                >
                  Buka halaman pelanggan
                </Button>
              )}
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </Box>
  );
}
